import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { api, ApiRequestError } from '@/lib/api';
import { describeApiError } from '../missions/_components/coverageFormatters';
import type { RotationControl, DetachMissionOutcome } from '../missions/_components/coverageTypes';

/**
 * THE FIVE THINGS AN OPERATOR CAN DO TO A ROTATION FROM THE COP.
 *
 * Every endpoint here already existed and is `requireRole('admin', 'operator')`. Nothing new is
 * added to the server by this hook - what is new is that they are reachable without leaving the
 * live air picture, which is the whole point of the feature (#505 note 7).
 *
 * FORCE DETACH IS NOT HERE, deliberately. `DELETE /api/coverage/:id/force` is admin-only and
 * bypasses the airborne gate - it is the escape hatch for a stuck row, not an operating control,
 * and it belongs on the mission page where the operator has the full panel around them. When the
 * ordinary detach is refused because an aircraft is up, this hook says so and names that page
 * rather than quietly offering the override on a 320 px strip.
 *
 * WHAT THE ANSWER IS USED FOR is as important as the request. Every path re-reads, because the
 * server's refusals here ARE the operator's next action: "a different aircraft is now proposed"
 * tells them to look again, and a toast saying "failed" would not. That is copied from
 * `MissionCoveragePanel.runControl`, whose comment makes the argument.
 */

/**
 * Every write here is bounded, and that is not belt-and-braces.
 *
 * `submitting` is the mutex for the whole block: while it is set, Approve, Rotate now, Cancel,
 * Suspend/Resume and Detach are all inert. A request that never settles therefore does not just
 * lose its own answer - it kills every control on the surface, permanently, with the button frozen
 * on "Approving...". The only recovery is deselecting and reselecting the mission, which is not
 * something anyone guesses at 02:00. `useMissionCommit` bounds every one of its writes for this
 * exact reason.
 */
const CONTROL_TIMEOUT_MS = 10_000;

/** What is in flight, so exactly one control can be pressed and each can name itself. */
export type CopRotationSubmitting = null | RotationControl | 'detach';

export interface CopRotationControls {
  submitting: CopRotationSubmitting;
  busy: boolean;
  /**
   * The ordinary detach was refused because an aircraft is airborne on this rotation.
   *
   * Kept as state rather than a toast: it is a REFUSAL WITH A NEXT STEP, and the next step is on
   * another page. A toast would take the sentence away after four seconds, which on the one
   * control here that can be refused for a safety reason is the wrong lifetime.
   */
  airborneRefusal: string | null;
  clearAirborneRefusal: () => void;
  approve: (actionId: string, droneId: string, plannedAt: string) => void;
  rotateNow: (actionId: string, droneId: string) => void;
  cancelReplacement: (actionId: string) => void;
  suspend: () => void;
  resume: () => void;
  detach: () => void;
}

export function useCopRotationControls(
  missionId: string | null,
  /** Re-read the rotation and the chart. Called on every path, success or failure. */
  onChanged: () => void,
  /**
   * Disarm. Called on every terminal outcome of every write, success or failure.
   *
   * SEPARATE FROM `onChanged` ON PURPOSE. That one also fires for the poll and for socket frames,
   * so an arm made a moment ago would be cleared by traffic the operator did not cause. This one
   * fires only for a request this hook itself started and has now finished.
   *
   * It exists because a refused detach left the arm standing: the 409 came back, the button still
   * read "Confirm detach", and the only thing on screen that had changed was a refusal rendered
   * below the fold. The operator pressed it again - which is exactly what the dev log shows.
   */
  onSettled: () => void,
): CopRotationControls {
  const [submitting, setSubmitting] = useState<CopRotationSubmitting>(null);
  const [airborneRefusal, setAirborneRefusal] = useState<string | null>(null);

  const runControl = useCallback(async (
    control: RotationControl,
    body: Record<string, unknown> | undefined,
    success: string,
  ) => {
    if (!missionId) return;
    setSubmitting(control);
    try {
      await api.post(`/api/coverage/${missionId}/rotation/${control}`, body ?? {}, {
        timeoutMs: CONTROL_TIMEOUT_MS,
      });
      toast.success(success);
      // A standing "an aircraft is airborne" refusal describes a moment that has since moved on -
      // suspending or approving does not make it true again, and it was outliving its own subject
      // indefinitely because only Dismiss and another detach ever cleared it.
      setAirborneRefusal(null);
    } catch (err) {
      // The server's sentence, not a generic failure. "The proposal moved" and "a different
      // aircraft is now proposed" are instructions; "The rotation control could not be applied"
      // is not.
      toast.error(describeApiError(err, 'The rotation control could not be applied'));
    } finally {
      // BOTH PATHS RE-READ. Whatever moved underneath the operator, this surface is now behind,
      // and re-reading is what turns the server's refusal from an assertion into something they
      // can see on the ribbon and the readings.
      // DISARM FIRST. The arm belongs to a press that is now finished, whichever way it went, and
      // leaving it standing is what turned a refusal into a second identical request.
      onSettled();
      onChanged();
      setSubmitting(null);
    }
  }, [missionId, onChanged, onSettled]);

  /**
   * APPROVE THE PROPOSAL ON SCREEN. The request carries the action, the aircraft and the time this
   * block is currently rendering, and the server refuses if any of them has moved.
   *
   * The caller passes all three rather than this hook reading them, because the values that must
   * be sent are the ones that were DRAWN - the mission panel learned this the hard way: taking the
   * time from the polled row instead of the rendered forecast meant a launch the planner brought
   * earlier was approved with a stale time and refused with "the launch time has moved since this
   * was shown", about a time that had been on the operator's screen unchanged the whole while.
   */
  const approve = useCallback((actionId: string, droneId: string, plannedAt: string) => {
    void runControl(
      'approve',
      { action_id: actionId, drone_id: droneId, planned_at: plannedAt },
      'Replacement approved - it is armed, not launched',
    );
  }, [runControl]);

  /**
   * BRING THE LAUNCH TO THIS INSTANT.
   *
   * No `planned_at`, unlike Approve: that one agrees to a time the planner chose, so it must send
   * the time the operator saw. This one REPLACES that time with the server's `now`, and there is
   * nothing on screen to have misread. It does not launch anything by itself - the orchestrator's
   * next sweep does, applying every gate.
   */
  const rotateNow = useCallback((actionId: string, droneId: string) => {
    void runControl(
      'rotate_now',
      { action_id: actionId, drone_id: droneId },
      'Replacement brought forward - the launch is due now',
    );
  }, [runControl]);

  const cancelReplacement = useCallback((actionId: string) => {
    void runControl('abort_replacement', { action_id: actionId }, 'Replacement cancelled');
  }, [runControl]);

  const suspend = useCallback(() => {
    void runControl('suspend', undefined, 'Rotation suspended - no replacement is being scheduled');
  }, [runControl]);

  const resume = useCallback(() => {
    void runControl('resume', undefined, 'Rotation resumed');
  }, [runControl]);

  /**
   * STAND THE STATION DOWN.
   *
   * Detaching has a SECOND, SEPARATE effect on the mission, and three of its four outcomes are not
   * "fine" - two of them need the operator to go and do something, so they are not a success
   * toast. The wording is the mission panel's, verbatim: two surfaces telling an operator
   * different things about one irreversible action is its own defect.
   */
  const detach = useCallback(async () => {
    if (!missionId) return;
    setSubmitting('detach');
    setAirborneRefusal(null);
    try {
      const res = await api.delete<{ data: { deleted: boolean; mission_outcome: DetachMissionOutcome } }>(
        `/api/coverage/${missionId}`,
        { timeoutMs: CONTROL_TIMEOUT_MS },
      );
      const outcome = res.data.mission_outcome;
      if (outcome === 'completed') {
        toast.success('Coverage detached - the mission is now complete');
      } else if (outcome === 'completion_deferred') {
        toast.warning('Coverage detached. This mission is still running - complete it once the aircraft is down.');
      } else if (outcome === 'completion_failed') {
        toast.error('Coverage detached, but the mission could not be completed. Complete it from the mission page.');
      } else {
        toast.success('Continuous coverage detached');
      }
    } catch (err) {
      // KEYED ON THE CODE, NEVER ON THE SENTENCE. That wording is the server's to change, and a
      // 500 must not be read as a safety refusal. The sentence is then kept anyway, because it is
      // the only thing that names the aircraft.
      if (err instanceof ApiRequestError && err.code === 'rotation_airborne') {
        setAirborneRefusal(describeApiError(err, 'An aircraft is airborne on this rotation'));
      } else {
        toast.error(describeApiError(err, 'Coverage could not be detached'));
      }
    } finally {
      // DISARM FIRST. The arm belongs to a press that is now finished, whichever way it went, and
      // leaving it standing is what turned a refusal into a second identical request.
      onSettled();
      onChanged();
      setSubmitting(null);
    }
  }, [missionId, onChanged, onSettled]);

  return {
    submitting,
    busy: submitting !== null,
    airborneRefusal,
    clearAirborneRefusal: useCallback(() => setAirborneRefusal(null), []),
    approve,
    rotateNow,
    cancelReplacement,
    suspend,
    resume,
    detach,
  };
}
